import { Box, Button, Flex, Text, BoxProps, useOutsideClick, useDisclosure } from '@chakra-ui/react';
import React, { FunctionComponent, ReactNode, useEffect, useState } from 'react';
import { FormatOptionLabelMeta } from 'react-select';
import { OptionType, Select, SelectedOption } from '..';
import { DownChevron, LockIcon, Magnifier } from '../../Icons';
import { CurrencyFlag } from '../CurrencyFlag';

interface CurrencyOptionProps {
  readonly currencyCode: string;
  readonly currencyName: string;
}

const CurrencyOption = ({ currencyCode, currencyName }: CurrencyOptionProps) => (
  <Flex alignItems="center" paddingX="16px" paddingY="12px" cursor="pointer">
    <Box>
      <CurrencyFlag currencyCode={currencyCode} size={6} />
    </Box>
    <Box minWidth={50} textAlign="center">
      <Text fontWeight="semibold" fontSize="lg">
        {currencyCode}
      </Text>
    </Box>
    <Box>
      <Text fontSize="sm">{currencyName}</Text>
    </Box>
  </Flex>
);

export interface CurrencyOption {
  readonly value: string;
  readonly label: string;
}

export interface GroupedCurrencyOption {
  readonly label: string;
  readonly options: CurrencyOption[];
}

interface CurrencySelectProps {
  readonly value: CurrencyOption;
  readonly currencies: GroupedCurrencyOption[];
  readonly onSelect: (value: CurrencyOption) => void;
  readonly dropdownTarget?: React.FC<React.PropsWithChildren<DropdownTargetProps>>;
  readonly isInvalid: boolean;
  readonly isDisabled?: boolean;
  readonly isLocked?: boolean;
  readonly width?: React.CSSProperties['width'];
  readonly anchor?: 'left' | 'right';
}

const formatOptionLabel: (option: OptionType, labelMeta: FormatOptionLabelMeta<OptionType, false>) => ReactNode = (
  option,
  { context, selectValue },
) => {
  if (context === 'menu') {
    return selectValue && selectValue[0]?.value === option.value ? (
      <SelectedOption>
        <CurrencyOption currencyCode={option.value} currencyName={option.label} />
      </SelectedOption>
    ) : (
      <CurrencyOption currencyCode={option.value} currencyName={option.label} />
    );
  }
};

export const CurrencySelect = ({
  value,
  currencies,
  onSelect,
  dropdownTarget = DropdownButton,
  anchor = 'right',
  width,
  isInvalid = false,
  isLocked = false,
  isDisabled = false,
}: CurrencySelectProps) => {
  const DropdownTarget = dropdownTarget;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentValue, setCurrentValue] = useState<CurrencyOption>(value);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  const onSelectChange = (option: CurrencyOption | null) => {
    setCurrentValue(option || value);
    onSelect(option || value);
    onClose();
  };

  return (
    <Dropdown
      anchor={anchor}
      width={width}
      isOpen={isOpen}
      onClose={onClose}
      target={
        <DropdownTarget
          isOpen={isOpen}
          isInvalid={isInvalid}
          isDisabled={isDisabled}
          isLocked={isLocked}
          toggleOpen={onOpen}
          currencyCode={currentValue.value}
        />
      }
    >
      <Select
        autoFocus
        backspaceRemovesValue={false}
        components={{ DropdownIndicator, IndicatorSeparator: null }}
        controlShouldRenderValue={false}
        hideSelectedOptions={false}
        isClearable={false}
        menuIsOpen
        onChange={onSelectChange}
        options={currencies}
        placeholder="Type a currency"
        tabSelectsValue={false}
        value={currentValue}
        formatOptionLabel={formatOptionLabel}
        variant="currencySelect"
      />
    </Dropdown>
  );
};

const SelectDropdown = ({ anchor, ...props }: BoxProps & { anchor: 'left' | 'right' }) => {
  return (
    <Box
      position="absolute"
      minWidth="257px"
      width="257px"
      backgroundColor="white"
      padding="4px 16px 16px 16px"
      boxShadow="xl"
      borderRadius="8px"
      zIndex={2}
      marginTop="4px"
      left={anchor === 'left' ? 0 : undefined}
      right={anchor === 'right' ? 0 : undefined}
      {...props}
    />
  );
};
interface DropdownProps {
  readonly anchor: 'left' | 'right';
  readonly isOpen: boolean;
  readonly target: ReactNode;
  readonly onClose: () => void;
  readonly width: React.CSSProperties['width'];
  children: ReactNode
}

const Dropdown = ({ children, anchor, isOpen, target, onClose, width = '100%' }: DropdownProps) => {
  const boxRef = React.useRef<any>();

  useOutsideClick({
    ref: boxRef,
    handler: onClose,
  });

  return (
    <Box ref={boxRef} position="relative" w={width} display="inline-block">
      {target}
      {isOpen ? <SelectDropdown anchor={anchor}>{children}</SelectDropdown> : null}
    </Box>
  );
};

const DropdownIndicator = () => (
  <Box color="text.tint">
    <Magnifier width="16px" height="16px" />
  </Box>
);

interface DropdownTargetProps {
  readonly isOpen: boolean;
  readonly isInvalid: boolean;
  readonly isLocked: boolean;
  readonly isDisabled?: boolean;
  readonly toggleOpen: () => void;
  readonly currencyCode: string;
}

const DropdownButton = ({ toggleOpen, currencyCode, isDisabled = false, isLocked = false }: DropdownTargetProps) => {
  const Icon = () =>
    isLocked ? (
      <LockIcon ml={{ base: 1, md: 2 }} width="14px" height="14px" color={'gray.500'} />
    ) : (
      <DownChevron ml={{ base: 1, md: 2 }} color={isDisabled ? 'gray.500' : 'primary.base'} />
    );

  return (
    <Button
      bgColor={'white'}
      borderColor={'gray.300'}
      border="none"
      borderRadius="md"
      color={'text.base'}
      _disabled={{ opacity: 1, cursor: 'not-allowed' }}
      _hover={{ bgColor: 'secondary.light', _disabled: { bgColor: 'white' } }}
      _focus={{ bgColor: 'secondary.light', _disabled: { bgColor: 'white' } }}
      _active={{ bgColor: 'secondary.light', _disabled: { bgColor: 'white' } }}
      py={{ base: '24px', md: '28px' }}
      width={{ base: '120px', md: '152px' }}
      fontSize={{ base: 'lg', md: '3xl' }}
      leftIcon={<CurrencyFlag currencyCode={currencyCode} size={{ base: 6, md: 7 }} />}
      rightIcon={<Icon />}
      onClick={isDisabled || isLocked ? undefined : toggleOpen}
      disabled={isDisabled}
    >
      {currencyCode}
    </Button>
  );
};
